import { Alert, Button, Form, Input, InputNumber, Modal, Select, Space, Table, Tag } from "@arco-design/web-react";

import { useCurrentAuthUser } from "../../auth";
import { canUsePermission } from "../../../lib/auth";
import { useBusVehicleTypeRuntime } from "../runtime/useBusVehicleTypeRuntime";
import type { BusVehicleTypeItem } from "../types";

/** 大巴车型字典维护页，字段和保存状态由 runtime 管理。 */
export function BusVehicleTypeFeaturePage() {
  const user = useCurrentAuthUser().data;
  const canManage = canUsePermission(user, "dictionary.bus_vehicle_type:manage");
  const runtime = useBusVehicleTypeRuntime(canManage);
  const { draft, fieldErrors } = runtime;
  return <main className="flex flex-col gap-4 p-6">
    <div><h1 className="text-xl font-semibold">大巴车型字典</h1><p className="mt-1 text-sm text-gray-500">维护人工核价可选车型，停用后不影响已有核价明细。</p></div>
    <Space wrap>
      <Input aria-label="搜索车型名称" placeholder="车型名称" allowClear style={{ width: 240 }} value={runtime.draftFilters.keyword}
        onChange={(keyword) => runtime.setDraftFilters({ ...runtime.draftFilters, keyword })} onPressEnter={runtime.search} />
      <Select aria-label="启停状态" style={{ width: 130 }} value={runtime.draftFilters.status}
        options={[{ value: "", label: "全部状态" }, { value: "active", label: "启用" }, { value: "disabled", label: "停用" }]}
        onChange={(status: string) => runtime.setDraftFilters({ ...runtime.draftFilters, status })} />
      <Button type="primary" onClick={runtime.search}>查询</Button><Button onClick={runtime.reset}>重置</Button>
      <Select aria-label="排序字段" style={{ width: 130 }} value={runtime.params.sortField}
        options={[{ value: "sort_no", label: "按排序号" }, { value: "seat_count", label: "按座位数" }, { value: "name", label: "按名称" }, { value: "updated_at", label: "按更新时间" }]}
        onChange={(value) => runtime.setSort(value, runtime.params.sortDirection)} />
      <Select aria-label="排序方向" style={{ width: 90 }} value={runtime.params.sortDirection}
        options={[{ value: "asc", label: "升序" }, { value: "desc", label: "降序" }]}
        onChange={(value) => runtime.setSort(runtime.params.sortField, value)} />
      {canManage && <Button type="primary" onClick={() => runtime.openEditor()}>新增车型</Button>}
    </Space>
    {runtime.queryError && <Alert type="error" content={runtime.queryError} action={<Button onClick={runtime.refresh}>重试</Button>} />}
    <Table<BusVehicleTypeItem> rowKey="id" loading={runtime.loading} data={runtime.items}
      pagination={{ current: runtime.params.page, pageSize: runtime.params.pageSize, total: runtime.total, showTotal: true, sizeCanChange: true, sizeOptions: [20, 50, 100], onChange: runtime.setPage }}
      columns={[
        { title: "车型名称", dataIndex: "name" }, { title: "座位数", dataIndex: "seatCount", width: 100 },
        { title: "排序", dataIndex: "sortNo", width: 100 },
        { title: "状态", dataIndex: "isDisabled", width: 100, render: (_, row) => <Tag color={row.isDisabled ? "gray" : "green"}>{row.isDisabled ? "停用" : "启用"}</Tag> },
        { title: "更新时间", dataIndex: "updatedAt", width: 200, render: (value: string) => value.replace("T", " ") },
        ...(canManage ? [{ title: "操作", width: 130, render: (_: unknown, row: BusVehicleTypeItem) => <Button type="text" onClick={() => runtime.openEditor(row)}>编辑 / 启停</Button> }] : []),
      ]} />
    <Modal title={runtime.editor?.id ? "编辑车型" : "新增车型"} visible={runtime.editor !== null}
      onCancel={runtime.closeEditor} onOk={() => { void runtime.save(); }} okText="保存" cancelText="取消"
      confirmLoading={runtime.saving} okButtonProps={{ disabled: !canManage }} cancelButtonProps={{ disabled: runtime.saving }}
      closable={!runtime.saving} maskClosable={!runtime.saving} escToExit={!runtime.saving} unmountOnExit>
      {runtime.errorMessage && <Alert type="error" content={runtime.errorMessage} className="mb-4" />}
      {!canManage && <Alert type="warning" content="维护权限已变更，当前输入仍保留。" className="mb-4" />}
      <Form layout="vertical" disabled={runtime.saving || !canManage}>
        <Form.Item label="车型名称" required validateStatus={fieldErrors.name ? "error" : undefined} help={fieldErrors.name}>
          <Input aria-label="车型名称" value={draft.name} onChange={(name) => runtime.setDraft({ ...draft, name })} placeholder="例如：49座大巴" />
        </Form.Item>
        <Form.Item label="座位数" required validateStatus={fieldErrors.seatCount ? "error" : undefined} help={fieldErrors.seatCount}>
          <InputNumber aria-label="座位数" style={{ width: "100%" }} value={Number.isFinite(draft.seatCount) ? draft.seatCount : undefined}
            onChange={(seatCount) => runtime.setDraft({ ...draft, seatCount: seatCount ?? Number.NaN })} hideControl />
        </Form.Item>
        <Form.Item label="排序" required validateStatus={fieldErrors.sortNo ? "error" : undefined} help={fieldErrors.sortNo}>
          <InputNumber aria-label="排序" style={{ width: "100%" }} value={Number.isFinite(draft.sortNo) ? draft.sortNo : undefined}
            onChange={(sortNo) => runtime.setDraft({ ...draft, sortNo: sortNo ?? Number.NaN })} hideControl />
        </Form.Item>
        <Form.Item label="状态">
          <Select aria-label="车型状态" value={draft.isDisabled ? "disabled" : "active"}
            options={[{ value: "active", label: "启用" }, { value: "disabled", label: "停用" }]}
            onChange={(value) => runtime.setDraft({ ...draft, isDisabled: value === "disabled" })} />
        </Form.Item>
      </Form>
    </Modal>
  </main>;
}
